import { Injector } from '@angular/core';
import { SubscriptionManager } from '@sama/rxjs-core';
import { Camera, Vector3 } from 'three';
import { MouseEventService } from '../../../../cross-team-shared/misc-services/events/mouse-event.service';
import { assertDefined } from '../../../../cross-team-shared/misc-utils/strict-record';
import { EventTrackingValue } from '../../../../team-apis/core/event-tracking';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../../team-apis/core/shell-connector';
import {
  Point2D,
  Point3D,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import { EditPanelCamera } from '../../../render/cameras/edit-panel-camera';
import { SideViewCamerasService } from '../../../render/cameras/side-view-cameras.service';
import { CuboidLocationState3D } from '../../../state/cuboid-location-state-3d';
import { PointCloudContentStore } from '../../../state/point-cloud-content-store';
import { getShapeForEventTracking } from '../../../utils/misc-utils';
import { vectorToPoint } from '../../../utils/point-cloud-utils';
import { Axis } from '../../side-view/side-view.component';
import { getContainerPoint } from './drag-handler-utils';

export interface EditPanelMouseState {
  world: Point3D;
  container: Point2D;
}

export abstract class AbstractEditPanelDragHandler extends SubscriptionManager {
  protected readonly store: PointCloudContentStore;
  protected readonly shellConnector: ShellConnector;
  protected readonly mouseEventService: MouseEventService;

  protected eventContext: EventTrackingValue;
  protected initialLocation: CuboidLocationState3D;
  protected initialMouseState: EditPanelMouseState;

  protected panelCamera!: EditPanelCamera;

  constructor(
    protected axis: Axis,
    injector: Injector,
    initialEvent: MouseEvent,
  ) {
    super();
    this.store = injector.get(PointCloudContentStore);
    this.shellConnector = injector.get(shellConnectorToken);
    this.mouseEventService = injector.get(MouseEventService);
    const sideViewCamerasService = injector.get(SideViewCamerasService);

    // build context at constructor time to ensure event has current state
    // values. Things like shape selection may change before the event is sent.
    this.eventContext = this.buildEventContext();
    this.initialLocation = this.store.getFocusedShapeLocation();

    if (this.shellConnector.hasFeatureFlag('new-edit-panel')) {
      // cast can be removed once the new edit panel is promoted
      this.panelCamera = sideViewCamerasService.getCameraByAxis(
        axis,
      ) as EditPanelCamera;
    }

    this.initialMouseState = {
      container: getContainerPoint(initialEvent),
      world: this.getWorldPoint(getContainerPoint(initialEvent)),
    };

    this.startListening();
  }

  complete() {
    this.unsubscribe();
    this.cleanUp();
    this.shellConnector.pushAndTrack({
      id: 'edit_shape_points',
      bucket: 'adjustment',
      ...this.eventContext,
      ...this.getTrackEvent(),
    });
  }

  protected cleanUp() {}

  protected getWorldPoint(containerPoint: Point2D) {
    const ndcAtCuboidCenter: Point3D = [
      containerPoint[0],
      containerPoint[1],
      // Pick the middle of the frustum's depth. With the camera frustum
      // near/far planes fitting the cuboid, this means that the resulting world
      // point will be co-planar to the cuboid's position.
      0,
    ];
    return ndcToWorld(ndcAtCuboidCenter, this.panelCamera);
  }

  private startListening() {
    this.subscribeTo(
      this.mouseEventService.getEvent$('container', 'mousemove', {
        modifiers: 'any',
      }),
      ({ event }) => {
        this.onDrag({
          world: this.getWorldPoint(getContainerPoint(event)),
          container: getContainerPoint(event),
        });
      },
    );
  }

  private buildEventContext() {
    const eventTrackingPanelName = {
      2: 'top',
      1: 'back',
      0: 'side',
    };

    const focusedCuboid = this.store.getFocusedShapeStore()?.state();
    assertDefined(focusedCuboid);

    return {
      shapes: [getShapeForEventTracking(focusedCuboid)],
      source: 'edit_panel',
      panel: eventTrackingPanelName[this.axis],
    };
  }

  protected abstract onDrag(mouseState: EditPanelMouseState): void;

  protected abstract getTrackEvent(): {
    method: string;
    sub_method?: string;
  } & EventTrackingValue;
}

const reusableVector = new Vector3();

function ndcToWorld(point: Point3D, camera: Camera) {
  return vectorToPoint(
    reusableVector.fromArray(point).setZ(0).unproject(camera),
  );
}
